import React, { useEffect, useState } from 'react';
import { observabilityService, deploymentService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { X, RefreshCw, Wrench, Key, CheckCircle, XCircle, AlertTriangle, ExternalLink, Settings, HelpCircle } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, Legend
} from 'recharts';
import { format } from 'date-fns';
import './DeploymentMetricsModal.css';

const RANGES = ['1h', '6h', '24h', '7d'];

const CHECK_ICON = { ok: <CheckCircle size={13} color="var(--success)" />, warn: <AlertTriangle size={13} color="var(--warning)" />, fail: <XCircle size={13} color="var(--danger)" /> };

const PROCESS_STATUS_ICON = {
  healthy:  <CheckCircle size={13} color="var(--success)" />,
  warn:     <AlertTriangle size={13} color="var(--warning)" />,
  critical: <XCircle size={13} color="var(--danger)" />,
  down:     <XCircle size={13} color="var(--danger)" />,
};

const formatUptime = (seconds) => {
  if (seconds == null) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export default function DeploymentMetricsModal({ deployment: dep, onClose, onRefresh }) {
  const { isAdmin } = useAuth();
  const [range, setRange]     = useState('1h');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [agentKey, setAgentKey] = useState(null);
  const [showKey, setShowKey]   = useState(false);

  const [showProcessConfig, setShowProcessConfig] = useState(false);
  const [processConfigText, setProcessConfigText] = useState('');
  const [processConfigError, setProcessConfigError] = useState(null);
  const [savingProcessConfig, setSavingProcessConfig] = useState(false);

  const loadMetrics = async (r) => {
    setLoading(true);
    try {
      const res = await observabilityService.getDeploymentMetrics(dep._id, r);
      setData(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadMetrics(range); }, [dep._id, range]);

  const openProcessConfig = () => {
    setProcessConfigText(JSON.stringify(dep.watchedProcesses || [], null, 2));
    setProcessConfigError(null);
    setShowProcessConfig(true);
  };

  const saveProcessConfig = async () => {
    let parsed;
    try {
      parsed = JSON.parse(processConfigText);
      if (!Array.isArray(parsed)) throw new Error('Must be a JSON array');
    } catch (err) {
      setProcessConfigError(`Invalid JSON: ${err.message}`);
      return;
    }
    setSavingProcessConfig(true);
    try {
      await deploymentService.update(dep._id, { watchedProcesses: parsed });
      setShowProcessConfig(false);
      onRefresh();
    } catch (err) {
      setProcessConfigError(err.response?.data?.message || err.message);
    } finally {
      setSavingProcessConfig(false);
    }
  };

  const handleRegenerateKey = async () => {
    if (!window.confirm('Regenerate agent key? The agent will need to be updated.')) return;
    const res = await deploymentService.regenerateKey(dep._id);
    setAgentKey(res.data.agentKey);
    setShowKey(true);
  };

  const handleMaintenance = async () => {
    const enable = !dep.maintenanceMode;
    await deploymentService.setMaintenance(dep._id, { enabled: enable, until: enable ? new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString() : null });
    onRefresh();
    onClose();
  };

  const STATUS_COLORS = { online: 'var(--success)', degraded: 'var(--warning)', critical: 'var(--danger)', offline: 'var(--danger)', unknown: 'var(--text-muted)' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="metrics-modal" onClick={e => e.stopPropagation()}>
        <div className="metrics-modal__header">
          <div>
            <div className="metrics-modal__title">{dep.name}</div>
            <div className="metrics-modal__sub">{dep.app?.name} · {dep.environment} · {dep.type}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="dep-status-badge" style={{ background: `${STATUS_COLORS[dep.currentStatus]}18`, color: STATUS_COLORS[dep.currentStatus] }}>
              ● {dep.currentStatus}
            </span>
            {(data?.grafanaUrl || dep.grafanaUrl) && (
              <a
                className="icon-btn"
                href={data?.grafanaUrl || dep.grafanaUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="View host metrics in Grafana"
              >
                <ExternalLink size={15} />
              </a>
            )}
            {isAdmin && (
              <button className="icon-btn" onClick={openProcessConfig} title="Configure watched processes">
                <Settings size={15} />
              </button>
            )}
            <button className="icon-btn" onClick={handleMaintenance} title={dep.maintenanceMode ? 'End maintenance' : 'Enable maintenance'}>
              <Wrench size={15} />
            </button>
            <button className="icon-btn" onClick={handleRegenerateKey} title="Regenerate agent key">
              <Key size={15} />
            </button>
            <button className="icon-btn" onClick={onClose}><X size={16} /></button>
          </div>
        </div>

        {showKey && agentKey && (
          <div className="agent-key-banner">
            <strong>New Agent Key:</strong>
            <code>{agentKey}</code>
            <span>Update your agent's .env file immediately</span>
          </div>
        )}

        {showProcessConfig && (
          <div className="process-config-banner">
            <div className="process-config-banner__label">
              Watched processes (JSON) — matches the agent's <code>WATCH_PROCESSES_JSON</code> config for this deployment
            </div>
            <textarea
              className="process-config-textarea"
              value={processConfigText}
              onChange={(e) => setProcessConfigText(e.target.value)}
              rows={8}
              spellCheck={false}
            />
            {processConfigError && <div className="process-config-error">{processConfigError}</div>}
            <div className="process-config-actions">
              <button className="btn-secondary" onClick={() => setShowProcessConfig(false)}>Cancel</button>
              <button className="btn-primary" onClick={saveProcessConfig} disabled={savingProcessConfig}>
                {savingProcessConfig ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}

        {/* Uptime + range */}
        <div className="metrics-modal__toolbar">
          <div className="uptime-chip">
            Uptime {data?.uptimePct != null ? `${data.uptimePct}%` : '—'}
            {data?.totalPoints != null && <span> · {data.totalPoints} pings</span>}
          </div>
          <div className="range-tabs">
            {RANGES.map(r => (
              <button key={r} className={`range-tab ${range === r ? 'active' : ''}`} onClick={() => setRange(r)}>{r}</button>
            ))}
          </div>
        </div>

        {loading && <div className="chart-loading">Loading metrics...</div>}

        {!loading && data?.series?.length === 0 && (
          <div className="chart-loading">No heartbeat data in this range</div>
        )}

        {!loading && data?.series?.length > 0 && (
          <div className="metrics-charts">
            <div className="chart-block">
              <div className="chart-label">CPU %</div>
              <ResponsiveContainer width="100%" height={100}>
                <LineChart data={data.series}>
                  <XAxis dataKey="ts" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis domain={[0,100]} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11 }} />
                  <ReferenceLine y={75} stroke="var(--warning)" strokeDasharray="3 3" />
                  <ReferenceLine y={90} stroke="var(--danger)"  strokeDasharray="3 3" />
                  <Line type="monotone" dataKey="cpu" stroke="var(--accent)" dot={false} strokeWidth={2} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-block">
              <div className="chart-label">Memory %</div>
              <ResponsiveContainer width="100%" height={100}>
                <LineChart data={data.series}>
                  <XAxis dataKey="ts" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis domain={[0,100]} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11 }} />
                  <ReferenceLine y={80} stroke="var(--warning)" strokeDasharray="3 3" />
                  <ReferenceLine y={95} stroke="var(--danger)"  strokeDasharray="3 3" />
                  <Line type="monotone" dataKey="mem" stroke="var(--success)" dot={false} strokeWidth={2} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-block">
              <div className="chart-label">Disk %</div>
              <ResponsiveContainer width="100%" height={100}>
                <LineChart data={data.series}>
                  <XAxis dataKey="ts" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis domain={[0,100]} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11 }} />
                  <ReferenceLine y={80} stroke="var(--warning)" strokeDasharray="3 3" />
                  <ReferenceLine y={90} stroke="var(--danger)"  strokeDasharray="3 3" />
                  <Line type="monotone" dataKey="disk" stroke="var(--warning)" dot={false} strokeWidth={2} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Watched processes — application-level view, the thing Grafana doesn't give you */}
        {data?.processes?.length > 0 && (
          <div className="processes-section">
            <div className="checks-title">Processes</div>
            <div className="processes-grid">
              {data.processes.map((p) => (
                <div key={p.key} className={`process-item process-item--${p.status}`}>
                  <div className="process-item__row">
                    {PROCESS_STATUS_ICON[p.status]}
                    <div className="process-item__name">
                      {p.label}
                      {p.critical && <span className="process-item__critical-tag">critical</span>}
                    </div>
                    <div className="process-item__stats">
                      {p.matched ? (
                        <>
                          <span>{p.cpuPercent != null ? `${p.cpuPercent}% cpu` : '—'}</span>
                          <span>{p.memMb != null ? `${p.memMb} MB` : '—'}</span>
                          <span>{formatUptime(p.uptimeSeconds)}</span>
                          <span title="Restarts observed within the configured window; resets if the agent itself restarts">
                            {p.restartCount || 0} restart{p.restartCount === 1 ? '' : 's'}
                            <HelpCircle size={11} style={{ marginLeft: 3, verticalAlign: -1, opacity: 0.6 }} />
                          </span>
                        </>
                      ) : (
                        <span>not running</span>
                      )}
                    </div>
                  </div>
                  {data.processSeries?.[p.key]?.length > 0 && (
                    <ResponsiveContainer width="100%" height={40}>
                      <LineChart data={data.processSeries[p.key]}>
                        <YAxis hide domain={[0, 'auto']} />
                        <Line type="monotone" dataKey="cpu" stroke="var(--accent)" dot={false} strokeWidth={1.5} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Checks */}
        {data?.checks?.length > 0 && (
          <div className="checks-section">
            <div className="checks-title">Health Checks (latest)</div>
            <div className="checks-grid">
              {data.checks.map((c, i) => (
                <div key={i} className={`check-item check-item--${c.status}`}>
                  {CHECK_ICON[c.status]}
                  <div>
                    <div className="check-name">{c.name}</div>
                    {c.message && <div className="check-msg">{c.message}</div>}
                  </div>
                  {c.latencyMs != null && <span className="check-latency">{c.latencyMs}ms</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
